




















































































import Vue from 'vue';
import i18n from 'vue-i18n';
import VuePortal from '@linusborg/vue-simple-portal';
import CoThumbnail from '@/components/Molecules/co-thumbnail/CoThumbnail.vue';
import CoHeading from '../../Atoms/co-heading/CoHeading.vue';
import CoRoundButton from '../../Atoms/co-round-button/CoRoundButton.vue';

Vue.use(VuePortal);
Vue.use(i18n);

export default {
    components: { CoThumbnail, CoHeading, CoRoundButton },
    name: 'CoModal',
    emits: ['close', 'show'],
    props: {
        // Named dialogs opt into shared keyboard containment and focus restoration.
        dialogLabelledby: {
            type: String,
            default: null,
        },
        title: {
            type: String,
            required: false,
            default: '',
        },
        titleClass: {
            type: String,
            required: false,
            default: '',
        },
        size: {
            type: String,
            required: false,
            default: 'md',
            validator(value: string) {
                return ['sm', 'md', 'lg', 'xl'].includes(value);
            },
        },
        background: {
            type: String,
            required: false,
            default: 'white',
            validator(value: string) {
                return ['default', 'white', 'grey'].includes(value);
            },
        },
        footer: {
            type: Boolean,
            required: false,
            default: true,
        },
        bodyClass: {
            type: [String, Object, Array],
            required: false,
            default: '',
        },
        titleImage: {
            type: String,
            required: false,
            default: '',
        },

        overflowY: {
            // if true, the modal will have overflow-y: auto
            // and the content will be scrollable
            type: String,
            required: false,
            default: 'auto',
            validator(value: string) {
                return ['auto', 'visible', 'hidden'].includes(value);
            },
        },
        maxHeight: {
            type: Boolean,
            required: false,
            default: false,
        },
        useAvailableHeight: {
            type: Boolean,
            required: false,
            default: false,
        },
        transparentBackground: {
            type: Boolean,
            required: false,
            default: false,
        },
        fullScreen: {
            type: Boolean,
            required: false,
            default: false,
        },
        closeOnClickAway: {
            type: Boolean,
            required: false,
            default: true,
        },
        disableClose: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    model: {
        event: 'close',
    },
    data() {
        return {
            showModal: false,
            previouslyFocusedElement: null as HTMLElement | null,
        };
    },
    computed: {
        classes() {
            return {
                [`${this.size}`]: true,
                [`${this.background}`]: true,
                'max-height': this.maxHeight,
                useAvailableHeight: this.useAvailableHeight,
                transparentBackground: this.transparentBackground,
                fullScreen: this.fullScreen,
            };
        },
        hasHeader() {
            return !!this.$slots.header;
        },
        hasBody() {
            return !!this.$slots.body;
        },
        hasFooter() {
            return !!this.$slots.footer;
        },
    },
    mounted() {
        this.$emit('shown', this);
    },
    watch: {
        showModal(val) {
            // disable body scroll when modal is open
            if (val) {
                document.documentElement.classList.add('prevent-scroll');
                document.body.classList.add('prevent-scroll');
            } else {
                document.documentElement.classList.remove('prevent-scroll');
                document.body.classList.remove('prevent-scroll');
            }
            if (!this.dialogLabelledby) return;
            if (val) {
                document.addEventListener('keydown', this.handleDialogKeydown);
                this.$nextTick(this.focusDialog);
            } else {
                document.removeEventListener('keydown', this.handleDialogKeydown);
                const focusTarget = this.previouslyFocusedElement;
                this.previouslyFocusedElement = null;
                this.$nextTick(() => {
                    if (focusTarget?.isConnected) focusTarget.focus();
                });
            }
        },
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleDialogKeydown);
        document.documentElement.classList.remove('prevent-scroll');
        document.body.classList.remove('prevent-scroll');
    },
    methods: {
        getFocusableElements() {
            const dialog = this.$refs.dialog as HTMLElement;
            return Array.from(
                dialog?.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
                ) || []
            ).filter(
                (element) =>
                    element.offsetParent !== null &&
                    !element.matches(':disabled') &&
                    (!element.hasAttribute('tabindex') || element.tabIndex >= 0)
            );
        },
        focusDialog() {
            const dialog = this.$refs.dialog as HTMLElement;
            if (dialog && (!dialog.contains(document.activeElement) || document.activeElement?.matches(':disabled'))) {
                (this.getFocusableElements()[0] || dialog).focus();
            }
        },
        handleDialogKeydown(event: KeyboardEvent) {
            const dialog = this.$refs.dialog as HTMLElement;
            // Only the topmost modal handles keys when dialogs are nested.
            const dialogs = document.querySelectorAll('.co-modal__content');
            if (!dialog || dialogs[dialogs.length - 1] !== dialog) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                this.discard();
            } else if (event.key === 'Tab') {
                const elements = this.getFocusableElements();
                const first = elements[0];
                const last = elements[elements.length - 1];
                if (!elements.length || !dialog.contains(document.activeElement) || document.activeElement === dialog) {
                    event.preventDefault();
                    (event.shiftKey ? last || dialog : first || dialog).focus();
                } else if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        },
        clickAway() {
            if (this.closeOnClickAway) this.discard();
        },
        discard() {
            if (this.disableClose) {
                return;
            }
            this.showModal = false;
            this.$emit('close');
        },

        show(focusTarget?: HTMLElement) {
            if (this.showModal) return;
            this.previouslyFocusedElement =
                focusTarget instanceof HTMLElement ? focusTarget : (document.activeElement as HTMLElement);
            this.showModal = true;
            this.$emit('show', this);
        },

        hide() {
            this.$emit('close');
            this.showModal = false;
        },
    },
};
